import { useContext, useEffect } from 'react';
import {
    GelScreenDetectorContext,
    GelRowLayout,
    GelContainerLite,
    GelParagraph,
    GelHeading2,
    GelSpinner,
    GelIcon,
    GelLabel
} from '@tal-gel/components';
import { getGelTokens } from '@tal-gel/theming';
import { UserContext } from '../../common/usercontext/user.context';
import Card from '../../common/components/card';
import ResponsiveContainer from "../../components/responsive.container";
import PageHeader from '../../common/components/PageHeader';
import { useGetHomePageContent } from '../../common/api/graphQLDataFetch';
import { useNavigate, useParams } from 'react-router-dom';
import { formatAsCurrency, formatDate, getGroupMembersByPaymentId } from './helpers';
import Spacer from '../../common/components/Spacer';
import { MMCTooltip } from '../../common/components/tooltip';
import HorizontalRule from '../../common/components/HorizontalRule';
import InlineAlert from '../../common/components/InlineAlert';
import { HistoricalPaymentDetail } from '../../common/models/memberclaims';

const PaymentDetails = () => {
    const { global: { sizeBaseX10, sizeBaseX3, sizeNone,
        sizeBaseX5, sizeBaseX8, sizeBaseX4, sizeBaseX2, themeColorIconDefault } } = getGelTokens();

    const { screen, isXsScreen } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;

    const { contextData, setUserContext } = useContext(UserContext)
    const navigate = useNavigate();
    const { data, isLoading } = useGetHomePageContent();

    const { paymentId } = useParams<{ paymentId: string }>();
    const paymentIdNumber = parseInt(paymentId ?? '', 10);
    const payments = getGroupMembersByPaymentId(paymentIdNumber, contextData?.historicalPayments ?? []);

    // Redirect if paymentId is invalid or payment is not found
    if (isNaN(paymentIdNumber) || !payments.length) {
        navigate('/payments');
        return null;
    }

    const getTitle = (payment: HistoricalPaymentDetail): string => {
        let isPartial = payment.paymentType === data?.payments?.Details?.PartialPayment?.Value;
        let suffix = isPartial ? data?.payments?.Details?.PartialPayment?.Text + ' ' : '';
        return `${suffix}${payment.claimType}`
    }

    if (isLoading) return <GelSpinner medium overlay />;

    return (
        <GelContainerLite style={{ padding: sizeNone, overflow: "hidden" }}>
            <PageHeader >
                <GelHeading2>{data?.payments?.Heading}</GelHeading2>
                <GelParagraph style={{ paddingTop: sizeBaseX3 }}>{data?.payments?.SubHeading}</GelParagraph>
            </PageHeader>

            <ResponsiveContainer>
                <div
                    onClick={() => navigate("/payments")}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        userSelect: "none",
                        paddingBottom: '24px',
                        paddingLeft: isXsScreen(screen) ? `${sizeBaseX5}px` : `${sizeBaseX10}px`,
                        paddingTop: isXsScreen(screen) ? `${sizeBaseX10}px` : `${sizeBaseX5}px`,
                    }}
                >
                    <GelIcon name={"ArrowLeft"} color={themeColorIconDefault} width="20px" height="20px" style={{ paddingLeft: 4, paddingRight: 4 }} />
                    <GelParagraph>Back</GelParagraph>
                </div>

                <GelRowLayout style={{ padding: isXsScreen(screen) ? `0px ${sizeBaseX5}px` : `4px ${sizeBaseX10}px`, marginBottom: '40px' }}>
                    <Card title={data?.payments?.Details?.Card?.Title || ''} showFooter={false}
                        toolTip={data?.payments?.Details?.Card?.ToolTip}
                        subTitle={data?.payments?.Details?.Card?.SubTitle}>
                        <GelContainerLite style={{ padding: `${sizeBaseX8}px ${sizeBaseX4}px`, display: 'flex', flexDirection: 'column', gap: '16px' }}>

                            {payments.length > 1 &&
                                <Card title={'Overview'} whiteHeader={true} showFooter={false}>
                                    <GelContainerLite style={{ padding: `${sizeBaseX8}px ${sizeBaseX4}px`, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <GelLabel>Payment Period</GelLabel>
                                            <GelParagraph>{formatDate(payments[0].benefitPaymentDateFrom)} to {formatDate(payments[0].benefitPaymentDateTo)}</GelParagraph>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <GelLabel>Total Gross Payment </GelLabel>
                                                <MMCTooltip selfAdaptive style={{ padding: isXsScreen(screen) ? "6px 4px 4px 0px" : "4px 4px 4px 0px", "marginRight": "4px" }}>
                                                    {data?.payments?.Details?.Card?.Overview.TotalGrossPaymentToolTip}
                                                </MMCTooltip>
                                            </div>
                                            <GelParagraph>{formatAsCurrency(payments.map(p => p.grossAmount).reduce((a, c) => a + c))}</GelParagraph>
                                        </div>
                                    </GelContainerLite>
                                </Card>}

                            {payments.map(payment =>
                                <Card key={payment.paymentId} title={getTitle(payment)}
                                    showFooter={false} lightHeader={true} >
                                    <GelContainerLite style={{ padding: `${sizeBaseX8}px ${sizeBaseX4}px`, display: 'flex', flexDirection: 'column', gap: '16px' }}>

                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <GelLabel>Payment Date </GelLabel>
                                                <MMCTooltip selfAdaptive style={{ padding: isXsScreen(screen) ? "6px 4px 4px 0px" : "4px 4px 4px 0px", "marginRight": "4px" }}>
                                                    {data?.payments?.Details?.PaymentDateToolTip}
                                                </MMCTooltip>
                                            </div>
                                            <GelParagraph>{formatDate(payment.paymentDate)}</GelParagraph>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <GelLabel>Payment Period</GelLabel>
                                            <GelParagraph>{formatDate(payment.dateFrom)} to {formatDate(payment.dateTo)}</GelParagraph>
                                        </div>
                                        <HorizontalRule />
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <GelLabel>Gross Payment </GelLabel>
                                                <MMCTooltip selfAdaptive style={{ padding: isXsScreen(screen) ? "6px 4px 4px 0px" : "4px 4px 4px 0px", "marginRight": "4px" }}>
                                                    {data?.payments?.Details?.GrossPaymentToolTip}
                                                </MMCTooltip>
                                            </div>
                                            <GelParagraph>{formatAsCurrency(payment.grossAmount)}</GelParagraph>
                                        </div>
                                        {(payment.adjustments.filter(a => (!data?.payments?.Details?.ExcludedAdjustments?.includes(a.type))).length > 0 || payment.netOffsetAmount !== 0) && (<>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <GelParagraph>{data?.payments?.Details?.OffsetsAndAdjustmentsLabel}</GelParagraph>
                                                <MMCTooltip selfAdaptive style={{ padding: isXsScreen(screen) ? "6px 4px 4px 0px" : "4px 4px 4px 0px", "marginRight": "4px" }}>
                                                    {data?.payments?.Details?.OffsetsAndAdjustmentsToolTip}
                                                </MMCTooltip>
                                            </div>
                                            {[...payment.adjustments, ...(payment.netOffsetAmount !== 0
                                                ? [{ type: data?.payments?.Details?.NetOffsetAmount || '', amount: payment.netOffsetAmount }]
                                                : [])]
                                                .filter(a => (!data?.payments?.Details?.ExcludedAdjustments?.includes(a.type)))
                                                .map((adj, index) => (
                                                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '16px' }}>
                                                        <GelLabel>{`${adj.type}`}</GelLabel>
                                                        <GelParagraph>{formatAsCurrency(adj.amount)}</GelParagraph>
                                                    </div>
                                                ))}
                                        </>)}
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <GelLabel>Net Payment</GelLabel>
                                                <MMCTooltip selfAdaptive style={{ padding: isXsScreen(screen) ? "6px 4px 4px 0px" : "4px 4px 4px 0px", "marginRight": "4px" }}>
                                                    {data?.payments?.Details?.NetPaymentToolTip}
                                                </MMCTooltip>
                                            </div>
                                            <GelParagraph>{formatAsCurrency(payment.netAmount)}</GelParagraph>
                                        </div>
                                    </GelContainerLite>
                                </Card>)}

                        </GelContainerLite>
                    </Card>

                        <InlineAlert variant="info" message={data?.payments?.Details?.PaymentDetailsMessage || ''} />

                </GelRowLayout>
            </ResponsiveContainer>

        </GelContainerLite>
    );
}

export default PaymentDetails;